package uz.mint.frontend.i18n

/**
 * Copy for the signed-in area: login, profile and My eSIMs.
 *
 * A separate table from [SiteStrings] rather than forty more fields on it. The landing copy
 * comes from the design file and is marketing; this is product UI, written here, and the two
 * change for different reasons. Same principle though — a data class, not a key→string map, so
 * a missing translation fails the build instead of rendering a blank.
 */
data class AccountStrings(
    val navAccount: String,
    val navLogin: String,
    val navLogout: String,

    // --- Login ---
    val loginMetaTitle: String,
    val loginMetaDescription: String,
    val loginTitle: String,
    val loginLede: String,
    val loginPhoneLabel: String,
    val loginPhonePlaceholder: String,
    val loginSend: String,
    val loginSending: String,
    val loginNote: String,

    val codeTitle: String,
    /** `{phone}` is the masked number the server echoed back. */
    val codeLede: String,
    val codeLabel: String,
    val codePlaceholder: String,
    val codeVerify: String,
    val codeVerifying: String,
    val codeResend: String,
    /** `{n}` seconds remaining before "resend" is allowed again. */
    val codeResendIn: String,
    val codeChangePhone: String,

    val errInvalidPhone: String,
    /** `{n}` attempts left. */
    val errWrongCode: String,
    val errExpired: String,
    val errBlocked: String,
    /** `{n}` seconds. */
    val errTooSoon: String,
    val errDeliveryFailed: String,
    val errGeneric: String,

    val welcomeNew: String,
    val welcomeBack: String,

    // --- Gate shown on account pages to a logged-out visitor ---
    val gateTitle: String,
    val gateBody: String,
    val gateCta: String,
    val gateLoading: String,

    // --- Profile ---
    val profileMetaTitle: String,
    val profileMetaDescription: String,
    val profileTitle: String,
    val profilePhone: String,
    val profilePhoneNote: String,
    val profileName: String,
    val profileNamePlaceholder: String,
    val profileLanguage: String,
    val profileSave: String,
    val profileSaving: String,
    val profileSaved: String,
    val profileSaveError: String,

    val sessionsTitle: String,
    val sessionsLede: String,
    val sessionsCurrent: String,
    val sessionsRevokeOthers: String,
    val sessionsRevoked: String,
    val sessionsEmpty: String,

    val ordersTitle: String,
    val ordersEmpty: String,
    /** One purchase in full, at /order?ref=… — reachable by tapping a row in the history. */
    val orderMetaTitle: String,
    val orderMetaDescription: String,
    val orderTitle: String,
    val orderNotFound: String,
    val orderBack: String,
    val orderPlanLabel: String,
    val orderPriceLabel: String,
    val orderMethodLabel: String,
    val orderDateLabel: String,
    val orderRefLabel: String,
    val orderEsimTitle: String,
    val orderEsimPending: String,
    val orderOpenDetails: String,

    // --- My eSIMs ---
    val esimsMetaTitle: String,
    val esimsMetaDescription: String,
    val esimsTitle: String,
    val esimsLede: String,
    val esimsLoading: String,
    val esimsError: String,
    val esimsRetry: String,
    val esimsEmpty: String,
    val esimsEmptyCta: String,
    val esimData: String,
    val esimRemaining: String,
    val esimExpires: String,
    /** `{n}` days left. */
    val esimDaysLeft: String,
    val esimExpired: String,
    val esimStatusLabel: String,
    val esimActivation: String,
    val esimActivationHelp: String,
    val esimCopy: String,
    val esimCopied: String,
    val esimPending: String,
    val esimQrShow: String,
    val esimQrTitle: String,
    /** Read on the full-screen view, where the customer is holding a second phone. */
    val esimQrHelp: String,
    /** For the case the QR cannot help with: the site is open on the phone being set up. */
    val esimQrSamePhone: String,
    val esimQrError: String,
    val esimQrClose: String,
    /** Link from the QR view into the step-by-step guide the footer already carries. */
    val esimInstallGuide: String,
    /** The provider's installation page — QR plus manual details — shown instead of the raw code. */
    val esimInstallLink: String,
    val esimInstallLinkHelp: String,
    val esimRefresh: String,
    val esimRefreshing: String,
    val esimOrderRef: String,

    private val statusLabels: Map<String, String>,
    private val orderStatusLabels: Map<String, String>,
) {
    /** Provider status codes are opaque strings; anything unmapped is shown as it arrived. */
    fun esimStatus(code: String?): String =
        code?.let { statusLabels[it.uppercase()] ?: it } ?: "—"

    fun orderStatus(code: String): String = orderStatusLabels[code.uppercase()] ?: code

    fun withCount(template: String, count: Int): String = template.replace("{n}", count.toString())

    fun withPhone(template: String, phone: String): String = template.replace("{phone}", phone)
}

private val AccountRussian = AccountStrings(
    navAccount = "Кабинет",
    navLogin = "Войти",
    navLogout = "Выйти",

    loginMetaTitle = "Вход — Mint.uz",
    loginMetaDescription = "Вход в личный кабинет Mint.uz по номеру телефона.",
    loginTitle = "Вход и регистрация",
    loginLede = "Введите номер телефона — пришлём код в SMS. Отдельная регистрация не нужна.",
    loginPhoneLabel = "Номер телефона",
    loginPhonePlaceholder = "90 123 45 67",
    loginSend = "Получить код",
    loginSending = "Отправляем…",
    loginNote = "Номер нужен, чтобы показывать ваши eSIM и историю покупок. Пароль не потребуется.",

    codeTitle = "Введите код",
    codeLede = "Отправили код на {phone}",
    codeLabel = "Код из SMS",
    codePlaceholder = "123456",
    codeVerify = "Подтвердить",
    codeVerifying = "Проверяем…",
    codeResend = "Отправить код ещё раз",
    codeResendIn = "Отправить ещё раз можно через {n} с",
    codeChangePhone = "Изменить номер",

    errInvalidPhone = "Это не похоже на узбекский мобильный номер",
    errWrongCode = "Неверный код. Осталось попыток: {n}",
    errExpired = "Срок действия кода истёк. Запросите новый",
    errBlocked = "Вход для этого номера закрыт. Напишите нам в Telegram",
    errTooSoon = "Слишком часто. Повторите через {n} с",
    errDeliveryFailed = "Не удалось отправить SMS. Попробуйте позже или напишите в Telegram",
    errGeneric = "Что-то пошло не так. Попробуйте ещё раз",

    welcomeNew = "Аккаунт создан",
    welcomeBack = "С возвращением",

    gateTitle = "Нужен вход",
    gateBody = "Эта страница доступна после входа по номеру телефона.",
    gateCta = "Войти",
    gateLoading = "Проверяем…",

    profileMetaTitle = "Профиль — Mint.uz",
    profileMetaDescription = "Личный кабинет Mint.uz: профиль, история заказов и активные сессии.",
    profileTitle = "Профиль",
    profilePhone = "Телефон",
    profilePhoneNote = "Это ваш логин — изменить его нельзя",
    profileName = "Имя",
    profileNamePlaceholder = "Как к вам обращаться",
    profileLanguage = "Язык",
    profileSave = "Сохранить",
    profileSaving = "Сохраняем…",
    profileSaved = "Сохранено",
    profileSaveError = "Не удалось сохранить",

    sessionsTitle = "Активные сессии",
    sessionsLede = "Устройства, на которых выполнен вход.",
    sessionsCurrent = "текущее устройство",
    sessionsRevokeOthers = "Выйти на остальных устройствах",
    sessionsRevoked = "Остальные устройства отключены",
    sessionsEmpty = "Активных сессий нет",

    ordersTitle = "История заказов",
    ordersEmpty = "Заказов пока нет",
    orderMetaTitle = "Заказ — Mint.uz",
    orderMetaDescription = "Детали покупки: тариф, оплата и eSIM с QR-кодом для установки.",
    orderTitle = "Заказ",
    orderNotFound = "Такой заказ не найден. Возможно, он оформлен на другом аккаунте.",
    orderBack = "Ко всем заказам",
    orderPlanLabel = "Тариф",
    orderPriceLabel = "Оплачено",
    orderMethodLabel = "Способ оплаты",
    orderDateLabel = "Дата",
    orderRefLabel = "Номер заказа",
    orderEsimTitle = "eSIM по этому заказу",
    orderEsimPending = "eSIM по этому заказу ещё выпускается. Обновите страницу через пару минут — QR-код появится здесь.",
    orderOpenDetails = "Подробнее",

    esimsMetaTitle = "Мои eSIM — Mint.uz",
    esimsMetaDescription = "Ваши eSIM: QR-код для установки, остаток трафика и срок действия.",
    esimsTitle = "Мои eSIM",
    esimsLede = "Здесь появляются купленные eSIM: код установки, остаток трафика и срок действия.",
    esimsLoading = "Загружаем…",
    esimsError = "Не удалось загрузить",
    esimsRetry = "Повторить",
    esimsEmpty = "Пока нет ни одной eSIM",
    esimsEmptyCta = "Выбрать тариф",
    esimData = "Трафик",
    esimRemaining = "осталось",
    esimExpires = "Действует до",
    esimDaysLeft = "осталось дней: {n}",
    esimExpired = "срок истёк",
    esimStatusLabel = "Статус",
    esimActivation = "Код установки",
    esimActivationHelp = "Скопируйте строку и вставьте её в настройках телефона: Сотовая связь → Добавить eSIM → Ввести вручную.",
    esimCopy = "Скопировать",
    esimCopied = "Скопировано",
    esimPending = "Код установки ещё готовится. Обновите страницу через пару минут.",
    esimQrShow = "Показать QR-код",
    esimQrTitle = "QR-код для установки",
    esimQrHelp = "Наведите камеру телефона, на который устанавливаете eSIM, на этот код.",
    esimQrSamePhone = "Сайт открыт на том же телефоне? Тогда QR не отсканировать — откройте страницу установки по ссылке ниже.",
    esimQrError = "Не удалось загрузить QR-код",
    esimQrClose = "Закрыть",
    esimInstallGuide = "Как установить eSIM",
    esimInstallLink = "Открыть страницу установки",
    esimInstallLinkHelp = "Перейдите по ссылке — там QR-код и данные для установки вручную.",
    esimRefresh = "Обновить",
    esimRefreshing = "Обновляем…",
    esimOrderRef = "Заказ",

    statusLabels = mapOf(
        "GOT_RESOURCE" to "готова к установке",
        "IN_USE" to "используется",
        "ENABLED" to "активна",
        "RELEASED" to "выпущена",
        "INSTALLATION" to "устанавливается",
        "USED_UP" to "трафик израсходован",
        "EXPIRED" to "срок истёк",
        "SUSPENDED" to "приостановлена",
        "REVOKED" to "отозвана",
        "CANCEL" to "отменена",
    ),
    orderStatusLabels = mapOf(
        "CREATED" to "ожидает оплаты",
        "PAID" to "оплачен",
        "PROVISIONING" to "выпускается",
        "COMPLETED" to "выполнен",
        "CANCELLED" to "отменён",
        "FAILED" to "ошибка",
    ),
)

private val AccountUzbek = AccountStrings(
    navAccount = "Kabinet",
    navLogin = "Kirish",
    navLogout = "Chiqish",

    loginMetaTitle = "Kirish — Mint.uz",
    loginMetaDescription = "Mint.uz shaxsiy kabinetiga telefon raqami orqali kirish.",
    loginTitle = "Kirish va roʻyxatdan oʻtish",
    loginLede = "Telefon raqamingizni kiriting — SMS orqali kod yuboramiz. Alohida roʻyxatdan oʻtish shart emas.",
    loginPhoneLabel = "Telefon raqami",
    loginPhonePlaceholder = "90 123 45 67",
    loginSend = "Kod olish",
    loginSending = "Yuborilmoqda…",
    loginNote = "Raqam eSIM'laringiz va xaridlar tarixini koʻrsatish uchun kerak. Parol talab qilinmaydi.",

    codeTitle = "Kodni kiriting",
    codeLede = "{phone} raqamiga kod yubordik",
    codeLabel = "SMS'dagi kod",
    codePlaceholder = "123456",
    codeVerify = "Tasdiqlash",
    codeVerifying = "Tekshirilmoqda…",
    codeResend = "Kodni qayta yuborish",
    codeResendIn = "Qayta yuborish {n} soniyadan keyin",
    codeChangePhone = "Raqamni oʻzgartirish",

    errInvalidPhone = "Bu Oʻzbekiston mobil raqamiga oʻxshamaydi",
    errWrongCode = "Kod notoʻgʻri. Qolgan urinishlar: {n}",
    errExpired = "Kod muddati tugadi. Yangisini soʻrang",
    errBlocked = "Bu raqam uchun kirish yopilgan. Telegram orqali yozing",
    errTooSoon = "Juda tez-tez. {n} soniyadan keyin urinib koʻring",
    errDeliveryFailed = "SMS yuborilmadi. Keyinroq urinib koʻring yoki Telegram orqali yozing",
    errGeneric = "Nimadir xato ketdi. Qayta urinib koʻring",

    welcomeNew = "Hisob yaratildi",
    welcomeBack = "Xush kelibsiz",

    gateTitle = "Kirish talab qilinadi",
    gateBody = "Bu sahifa telefon raqami orqali kirgandan soʻng ochiladi.",
    gateCta = "Kirish",
    gateLoading = "Tekshirilmoqda…",

    profileMetaTitle = "Profil — Mint.uz",
    profileMetaDescription = "Mint.uz kabineti: profil, buyurtmalar tarixi va faol seanslar.",
    profileTitle = "Profil",
    profilePhone = "Telefon",
    profilePhoneNote = "Bu sizning loginingiz — oʻzgartirib boʻlmaydi",
    profileName = "Ism",
    profileNamePlaceholder = "Sizga qanday murojaat qilaylik",
    profileLanguage = "Til",
    profileSave = "Saqlash",
    profileSaving = "Saqlanmoqda…",
    profileSaved = "Saqlandi",
    profileSaveError = "Saqlab boʻlmadi",

    sessionsTitle = "Faol seanslar",
    sessionsLede = "Kirilgan qurilmalar.",
    sessionsCurrent = "joriy qurilma",
    sessionsRevokeOthers = "Boshqa qurilmalardan chiqish",
    sessionsRevoked = "Boshqa qurilmalar oʻchirildi",
    sessionsEmpty = "Faol seanslar yoʻq",

    ordersTitle = "Buyurtmalar tarixi",
    ordersEmpty = "Hozircha buyurtmalar yoʻq",
    orderMetaTitle = "Buyurtma — Mint.uz",
    orderMetaDescription = "Xarid tafsilotlari: tarif, toʻlov va oʻrnatish uchun QR-kodli eSIM.",
    orderTitle = "Buyurtma",
    orderNotFound = "Bunday buyurtma topilmadi. Ehtimol, u boshqa akkauntda rasmiylashtirilgan.",
    orderBack = "Barcha buyurtmalar",
    orderPlanLabel = "Tarif",
    orderPriceLabel = "Toʻlangan",
    orderMethodLabel = "Toʻlov usuli",
    orderDateLabel = "Sana",
    orderRefLabel = "Buyurtma raqami",
    orderEsimTitle = "Shu buyurtma boʻyicha eSIM",
    orderEsimPending = "Bu buyurtma boʻyicha eSIM hali chiqarilmoqda. Bir necha daqiqadan soʻng sahifani yangilang — QR-kod shu yerda paydo boʻladi.",
    orderOpenDetails = "Batafsil",

    esimsMetaTitle = "Mening eSIM'larim — Mint.uz",
    esimsMetaDescription = "Sizning eSIM'laringiz: oʻrnatish uchun QR-kod, qolgan trafik va amal qilish muddati.",
    esimsTitle = "Mening eSIM'larim",
    esimsLede = "Sotib olingan eSIM'lar shu yerda: oʻrnatish kodi, qolgan trafik va muddat.",
    esimsLoading = "Yuklanmoqda…",
    esimsError = "Yuklab boʻlmadi",
    esimsRetry = "Qayta urinish",
    esimsEmpty = "Hozircha bironta eSIM yoʻq",
    esimsEmptyCta = "Tarif tanlash",
    esimData = "Trafik",
    esimRemaining = "qoldi",
    esimExpires = "Amal qiladi",
    esimDaysLeft = "qolgan kunlar: {n}",
    esimExpired = "muddati tugagan",
    esimStatusLabel = "Holati",
    esimActivation = "Oʻrnatish kodi",
    esimActivationHelp = "Qatorni nusxalab, telefon sozlamalariga qoʻying: Uyali aloqa → eSIM qoʻshish → Qoʻlda kiritish.",
    esimCopy = "Nusxalash",
    esimCopied = "Nusxalandi",
    esimPending = "Oʻrnatish kodi hali tayyorlanmoqda. Bir necha daqiqadan soʻng yangilang.",
    esimQrShow = "QR-kodni koʻrsatish",
    esimQrTitle = "Oʻrnatish uchun QR-kod",
    esimQrHelp = "eSIM oʻrnatilayotgan telefon kamerasini shu kodga qarating.",
    esimQrSamePhone = "Sayt oʻsha telefonda ochilganmi? Unda QR-ni skanerlab boʻlmaydi — quyidagi havola orqali oʻrnatish sahifasini oching.",
    esimQrError = "QR-kodni yuklab boʻlmadi",
    esimQrClose = "Yopish",
    esimInstallGuide = "eSIM qanday oʻrnatiladi",
    esimInstallLink = "Oʻrnatish sahifasini ochish",
    esimInstallLinkHelp = "Havolaga oʻting — u yerda QR-kod va qoʻlda oʻrnatish maʼlumotlari bor.",
    esimRefresh = "Yangilash",
    esimRefreshing = "Yangilanmoqda…",
    esimOrderRef = "Buyurtma",

    statusLabels = mapOf(
        "GOT_RESOURCE" to "oʻrnatishga tayyor",
        "IN_USE" to "ishlatilmoqda",
        "ENABLED" to "faol",
        "RELEASED" to "chiqarilgan",
        "INSTALLATION" to "oʻrnatilmoqda",
        "USED_UP" to "trafik tugagan",
        "EXPIRED" to "muddati tugagan",
        "SUSPENDED" to "toʻxtatilgan",
        "REVOKED" to "bekor qilingan",
        "CANCEL" to "bekor qilingan",
    ),
    orderStatusLabels = mapOf(
        "CREATED" to "toʻlov kutilmoqda",
        "PAID" to "toʻlangan",
        "PROVISIONING" to "chiqarilmoqda",
        "COMPLETED" to "bajarilgan",
        "CANCELLED" to "bekor qilingan",
        "FAILED" to "xatolik",
    ),
)

private val AccountEnglish = AccountStrings(
    navAccount = "Account",
    navLogin = "Sign in",
    navLogout = "Sign out",

    loginMetaTitle = "Sign in — Mint.uz",
    loginMetaDescription = "Sign in to your Mint.uz account with your phone number.",
    loginTitle = "Sign in or sign up",
    loginLede = "Enter your phone number and we'll text you a code. No separate sign-up needed.",
    loginPhoneLabel = "Phone number",
    loginPhonePlaceholder = "90 123 45 67",
    loginSend = "Send code",
    loginSending = "Sending…",
    loginNote = "We use your number to show your eSIMs and order history. No password required.",

    codeTitle = "Enter the code",
    codeLede = "We sent a code to {phone}",
    codeLabel = "Code from SMS",
    codePlaceholder = "123456",
    codeVerify = "Confirm",
    codeVerifying = "Checking…",
    codeResend = "Send the code again",
    codeResendIn = "You can resend in {n}s",
    codeChangePhone = "Change number",

    errInvalidPhone = "That doesn't look like an Uzbek mobile number",
    errWrongCode = "Wrong code. Attempts left: {n}",
    errExpired = "That code has expired. Request a new one",
    errBlocked = "Sign-in is closed for this number. Message us on Telegram",
    errTooSoon = "Too often. Try again in {n}s",
    errDeliveryFailed = "We couldn't send the SMS. Try later or message us on Telegram",
    errGeneric = "Something went wrong. Please try again",

    welcomeNew = "Account created",
    welcomeBack = "Welcome back",

    gateTitle = "Sign-in required",
    gateBody = "This page is available after signing in with your phone number.",
    gateCta = "Sign in",
    gateLoading = "Checking…",

    profileMetaTitle = "Profile — Mint.uz",
    profileMetaDescription = "Your Mint.uz account: profile, order history and active sessions.",
    profileTitle = "Profile",
    profilePhone = "Phone",
    profilePhoneNote = "This is your login — it can't be changed",
    profileName = "Name",
    profileNamePlaceholder = "What should we call you",
    profileLanguage = "Language",
    profileSave = "Save",
    profileSaving = "Saving…",
    profileSaved = "Saved",
    profileSaveError = "Couldn't save",

    sessionsTitle = "Active sessions",
    sessionsLede = "Devices that are currently signed in.",
    sessionsCurrent = "this device",
    sessionsRevokeOthers = "Sign out everywhere else",
    sessionsRevoked = "Other devices signed out",
    sessionsEmpty = "No active sessions",

    ordersTitle = "Order history",
    ordersEmpty = "No orders yet",
    orderMetaTitle = "Order — Mint.uz",
    orderMetaDescription = "Purchase details: the plan, the payment, and the eSIM with its installation QR code.",
    orderTitle = "Order",
    orderNotFound = "No such order. It may belong to a different account.",
    orderBack = "All orders",
    orderPlanLabel = "Plan",
    orderPriceLabel = "Paid",
    orderMethodLabel = "Payment method",
    orderDateLabel = "Date",
    orderRefLabel = "Order number",
    orderEsimTitle = "The eSIM for this order",
    orderEsimPending = "The eSIM for this order is still being issued. Refresh in a couple of minutes and the QR code will appear here.",
    orderOpenDetails = "Details",

    esimsMetaTitle = "My eSIMs — Mint.uz",
    esimsMetaDescription = "Your eSIMs: installation QR code, remaining data and validity.",
    esimsTitle = "My eSIMs",
    esimsLede = "Your purchased eSIMs live here: installation code, remaining data and validity.",
    esimsLoading = "Loading…",
    esimsError = "Couldn't load",
    esimsRetry = "Retry",
    esimsEmpty = "No eSIMs yet",
    esimsEmptyCta = "Browse plans",
    esimData = "Data",
    esimRemaining = "left",
    esimExpires = "Valid until",
    esimDaysLeft = "{n} days left",
    esimExpired = "expired",
    esimStatusLabel = "Status",
    esimActivation = "Installation code",
    esimActivationHelp = "Copy the string and paste it in your phone settings: Cellular → Add eSIM → Enter manually.",
    esimCopy = "Copy",
    esimCopied = "Copied",
    esimPending = "The installation code is still being issued. Refresh in a couple of minutes.",
    esimQrShow = "Show QR code",
    esimQrTitle = "Installation QR code",
    esimQrHelp = "Point the camera of the phone you are installing the eSIM on at this code.",
    esimQrSamePhone = "Reading this on the same phone? Then the QR cannot be scanned — open the installation page from the link below.",
    esimQrError = "Could not load the QR code",
    esimQrClose = "Close",
    esimInstallGuide = "How to install an eSIM",
    esimInstallLink = "Open the installation page",
    esimInstallLinkHelp = "Follow the link — it has the QR code and the manual installation details.",
    esimRefresh = "Refresh",
    esimRefreshing = "Refreshing…",
    esimOrderRef = "Order",

    statusLabels = mapOf(
        "GOT_RESOURCE" to "ready to install",
        "IN_USE" to "in use",
        "ENABLED" to "active",
        "RELEASED" to "released",
        "INSTALLATION" to "installing",
        "USED_UP" to "data used up",
        "EXPIRED" to "expired",
        "SUSPENDED" to "suspended",
        "REVOKED" to "revoked",
        "CANCEL" to "cancelled",
    ),
    orderStatusLabels = mapOf(
        "CREATED" to "awaiting payment",
        "PAID" to "paid",
        "PROVISIONING" to "provisioning",
        "COMPLETED" to "completed",
        "CANCELLED" to "cancelled",
        "FAILED" to "failed",
    ),
)

fun accountStringsFor(locale: Locale): AccountStrings = when (locale) {
    Locale.RU -> AccountRussian
    Locale.UZ -> AccountUzbek
    Locale.EN -> AccountEnglish
}
